import {
  BRANDS,
  MARQUES,
  COLLECTIONS,
  ASSORTIMENT,
} from '@utils/plp/constants';
import { getCategorySlugFromName } from './getCategorySlugFromName';
import { getCategorySlugsFromUrl } from './getCategorySlugsFromUrl';

export interface NamedBreadcrumb {
  name: string;
  entityId?: number;
}

export interface ResolvedBreadcrumb {
  name: string;
  path: string;
  entityId?: number;
}

/**
 * Derives storefront breadcrumb hrefs from category names, mirroring the legacy
 * `components/algolia/Breadcrumbs` behaviour: cumulative name -> slug paths
 * (e.g. `products/mens/clothing`), with brand and custom-collection special
 * casing. The returned `path` has no locale prefix or leading slash, matching
 * the old component's `generateLink` input.
 */
export const getBreadcrumbHrefs = (
  breadcrumbs: NamedBreadcrumb[],
  locale = 'en'
): ResolvedBreadcrumb[] => {
  const names = breadcrumbs.map((crumb) => crumb.name);

  const breadcrumbsFromCategories = breadcrumbs
    .filter((crumb) => crumb.name !== 'all-products')
    .map((crumb, index) => ({
      name: crumb.name,
      entityId: crumb.entityId,
      path: names
        .slice(0, index + 1)
        .map((bcc) => {
          if (bcc === 'All') {
            return 'Products';
          }
          if (bcc === 'Tout') {
            return 'Produits';
          }
          return bcc;
        })
        .map((catName) => getCategorySlugFromName(catName, locale))
        .join('/'),
    }));

  // force brands slug to point to /brands
  let crumbs = breadcrumbsFromCategories.map((crumb) =>
    [BRANDS, MARQUES].includes(crumb.name.toLocaleLowerCase())
      ? { ...crumb, path: locale === 'en' ? BRANDS : MARQUES }
      : crumb
  );

  // For custom collections, only return relevant breadcrumbs
  const currentPath = breadcrumbsFromCategories.at(-1)?.path || '';
  const { categoryFilters } = getCategorySlugsFromUrl(currentPath, locale);
  const isCustomCollection = crumbs.some((crumb) =>
    [COLLECTIONS, ASSORTIMENT].includes(crumb.name.toLocaleLowerCase())
  );

  const numberOfBreadcrumbsToKeep = categoryFilters
    ? categoryFilters.length + 1
    : 1;
  if (isCustomCollection) {
    crumbs = [crumbs[0], ...crumbs.slice(-numberOfBreadcrumbsToKeep)];
  }

  return crumbs;
};
